import Vue from 'vue'
import {UserRoleEnum} from '@/enums/userRolesEnum';
import {Task} from '@/interfaces/TaskInterfaces';
import SocketMixin from "@/mixins/SocketMixin";
import RightPanelTasks from "@/components/tasks/rightPanel/RightPanelTasks";
import {HTTP} from "@/utils/httpCommonUtil";
import InvoicesEventHub from "@/components/invoices/InvoicesEventHub";
import AuthMixin from "@/mixins/AuthMixin";

export default Vue.extend({
  name: 'ApplicationShell',
  components: {
    RightPanelTasks
  },
  data () {
    return {
      mainContentTopColor: 'background-light-gray',
      selectedShadowTop: 69,
      selectedRootPath: 'calendar-shifts',
      UserRoleEnum: UserRoleEnum,
      showSideNav: false,
      temporaryValue: null,
      expiredTokenInterval: null as any,
      calendarShiftRouteActive: false,
      communicationRouteActive: false,
      taskRouteActive: false,
      taskSideNavExpanded: false,
      providersToPayCount: 0,
      unreadMessagesCount: 0,
      left: 0,
      originX: 0,
      originLeft: 0
    }
  },
  mixins: [SocketMixin, AuthMixin],
  computed: {
    providerText (): string {
      return this.isProviderUser ? 'Provider Profile' : 'Providers';
    },
    isProviderUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.PROVIDER);
    },
    isFacilityContactUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.FACILITY_CONTACT);
    },
    myTaskCount (): number {
      const appUserId = this.$store.state.user.appUserId;
      return this.$store.getters["task/getTasksForUserId"](appUserId).incomingTasks.filter((task: Task) => task.assignedToAppUserId === appUserId).length;
    },
    unreadTaskCount (): number {
      const appUserId = this.$store.state.user.appUserId;
      return this.$store.getters["task/getTasksForUserId"](appUserId).tasks.filter((task: Task) => !task.isRead).length;
    },
    semiCollapseSideNav (): boolean {
      return !this.taskRouteActive && (this.calendarShiftRouteActive || this.taskSideNavExpanded || this.communicationRouteActive);
    },
    isAccountantUser(): boolean {
      return this.$store.getters['user/hasRole']([UserRoleEnum.ACCOUNTANT, UserRoleEnum.MANAGER]);
    },
    refreshUnreadCount(): number {
      return this.$store.state.communication.leftNavRefreshCounter;
    }
  },
  watch: {
    async refreshUnreadCount() {
      await this.refreshUnreadMessagesCount();
    }
  },
  beforeMount: function () {
    // Semi collapse Side Nav if on Calendar Shifts View
    this.calendarShiftRouteActive = (this.$router as any).history.current.fullPath === "/calendar-shifts";
    this.communicationRouteActive = (this.$router as any).history.current.fullPath === "/communications";
    this.taskRouteActive = (this.$router as any).history.current.fullPath.includes("/tasks/");
  },
  mounted: function () {
    if (!this.$store.state.user.token) {
      this.$router.replace('/login');
      return;
    } else if ( this.$store.getters['user/isTokenExpired']() ) {
      this.$router.replace('/login');
    } else {
      this.route(this.$route.path);
    }
  },
  async created () {
    let self = this;
    this.expiredTokenInterval = setInterval(function () {
      if ( self.$store.getters['user/isTokenExpired']() ) {
        self.$notify({
          group: 'main',
          type: 'success',
          title: 'Your Session Has Expired',
          text: 'Please log in again.',
          duration: 5000
        });
        self.$router.replace('/login');
      }
    }, 1000 * 60);
    if (this.isAccountantUser){
      await this.refreshProvidersToPayCount();
      InvoicesEventHub.$on('PROVIDERS_TO_PAY_CHANGE', this.refreshProvidersToPayCount)
    }
    await this.refreshUnreadMessagesCount();
  },
  beforeDestroy: function () {
    clearInterval(this.expiredTokenInterval);
    if (this.isAccountantUser){
      InvoicesEventHub.$off('PROVIDERS_TO_PAY_CHANGE', this.refreshProvidersToPayCount)
    }

    this.onMouseUp();
  },
  updated: function () {
    this.route(this.$route.path);
  },
  methods: {
    hasRole (userRoleEnum: Array<UserRoleEnum>) {
      return this.$store.getters['user/hasRole'](userRoleEnum);
    },
    async refreshProvidersToPayCount () {
      try {
        const response = await HTTP.get('/api/provider-payments/queue?isReadyToPay=false&isCountOnly=true');
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Information.");
        }
        this.providersToPayCount = response.data.data[0]
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'Failed to update Providers To Pay Count.'
        });
      }
    },
    async refreshUnreadMessagesCount () {
      if (this.isProviderUser || this.isFacilityContactUser) {
        return;
      }
      try {
        const response = await HTTP.get('/api/communications/message-count');
        if (!response.data || (!response.data.data && response.data.data !== 0)) {
          throw new Error("Error retrieving Information.");
        }
        this.unreadMessagesCount = response.data.data
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'Failed to update Unread Messages Count.'
        });
      }
    },
    route (path: string) {
      const rootPath = this.getRootPath(path);
      this.selectedRootPath = rootPath;
      let selectedNavElement = document.getElementById(rootPath);
      let shadowBoxTop = selectedNavElement !== null ? selectedNavElement.offsetTop + 69 : 69;
      if ( this.calendarShiftRouteActive !== (rootPath === 'calendar-shifts') ) {
        setTimeout(() => {
          this.calendarShiftRouteActive = rootPath === 'calendar-shifts';
        }, 200);
      }
      if ( this.communicationRouteActive !== (rootPath === 'communications') ) {
        setTimeout(() => {
          this.communicationRouteActive = rootPath === 'communications';
        }, 200);
      }
      if ( this.taskRouteActive !== (rootPath === 'tasks') ) {
        this.taskSideNavExpanded = false;
        setTimeout(() => {
          this.taskRouteActive = rootPath === 'tasks';
        }, 200);
      }
      switch (rootPath) {
        case 'calendar-shifts':
          this.mainContentTopColor = 'background-purple';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'shifts':
          this.mainContentTopColor = 'background-purple';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'facilities':
          this.mainContentTopColor = 'background-red';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'providers':
          this.mainContentTopColor = 'background-blue';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'communications':
          this.mainContentTopColor = 'background-blue-red';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'invoices':
          this.mainContentTopColor = 'background-green';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'payments':
          this.mainContentTopColor = 'background-green';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'provider-payments':
          this.mainContentTopColor = 'background-green';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'settings':
          this.mainContentTopColor = 'background-yellow';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'tasks':
          this.mainContentTopColor = 'background-dark-gray';
          this.selectedShadowTop = shadowBoxTop;
          break;
        case 'reports':
          this.mainContentTopColor = 'background-report-blue';
          this.selectedShadowTop = shadowBoxTop;
          break;
        default:
          this.mainContentTopColor = 'background-light-gray';
          this.selectedShadowTop = -54;
          break;
      }
      if (path === '/providers' && this.isProviderUser) {
        path = `/providers/${this.$store.state.user.providerId}`
      }
      if (path === "/login") {
        this.$router.replace(path);
      } else {
        this.$router.push(path);
      }
    },
    async logout() {
      const theMixin = this as any; // Stupid hack for legacy Vue to shut up warnings for stuff in Mixin
      window.location.href = `${process.env.COGNITO_URL}/logout?client_id=${theMixin.getAppClientId()}&logout_uri=${theMixin.getLoginUrl()}`;
    },
    getRootPath (path: string) {
      let rootPath = path.split('/')[1];
      // Invoices might actually be "under" facilities path but should still show 'Invoice' color.
      if (path.indexOf('/invoices/') > -1) {
        return 'invoices';
      }
      return rootPath;
    },
    toggleSideNav () {
      this.showSideNav = !this.showSideNav;
    },
    hideSideNav () {
      this.showSideNav = false;
    },
    reactToTaskExpanded(taskSideNavExpanded: boolean) {
      this.taskSideNavExpanded = taskSideNavExpanded;
    },

    // Horizontal Scrolling related methods.
    onScroll(e: any) {
      this.left = e.srcElement.scrollLeft;
    },
    onMouseMove(e: any) {
      const offset = e.pageX - this.originX;
      const left = this.originLeft - offset;
      this.$nextTick(() => {
        const horizontal:any = this.$refs.horizontal;
        horizontal.scrollLeft = left;
      })
    },
    onMouseDown(e: any) {
      this.originX = e.pageX;
      this.originLeft = this.left;

      window.addEventListener("mouseup", this.onMouseUp);
      window.addEventListener("mousemove", this.onMouseMove);
    },
    onMouseUp() {
      window.removeEventListener("mouseup", this.onMouseUp);
      window.removeEventListener("mousemove", this.onMouseMove);
    }

  },
});



// WEBPACK FOOTER //
// ./src/components/applicationShell/ApplicationShell.ts